import React,{useEffect,useLayoutEffect,useRef,useState,useCallback} from 'react';
import useSetState from 'customHooks/setState';
import AudioControlsButton from 'components/common/AudioControlsButton';
import { useStore, StoreTypes } from 'context';
import classnames from 'classnames';
import styles from './index.module.scss';
import Repository from 'repositories/Repository';
import { ScrollCoordinate, ScrollPositionButtonStateType,ScrollPositionType, ScrollLength} from "constants/scrollPositionType";
import { ScrollButtonStateBehavior,scrollPositionButtonBehavior } from "../ExtendedContent/ExtendedContentScrollPositionButton";
import * as types from "constants/actionTypes";
import { EventBus, EventBusType } from 'events/EventBus';
import { CanvasEvent } from 'events/EventTypes';
import { SplitType } from 'constants/splitType';
import { mergeLineBands, splitIntoPages } from './paging';

const { ExtendedResourceRepository } = Repository;

// 切頁模式：題目圖片最多佔頁面高度的比例（只在第一頁顯示）
const IMAGE_MAX_RATIO = 0.5;

const HearingDraft = ({ data, defaultTab }) => {
  const { contents } = data;
  const [{ bookId }] = useStore(StoreTypes.books);
  const [{ fontSize,scrollPosition, shouldSplited, splitType, currentPage }, extendedContentDispatch] = useStore(StoreTypes.extendedContent);
  const [{ tabIndex }, setState] = useSetState({
    tabIndex: defaultTab
  });
  const topicRef = useRef()
  const panelRef = useRef()
  const titleRef = useRef()
  const contentRef = useRef()
  const innerRef = useRef()
  // 切頁模式算出的各頁範圍 [{ start, end }]，以內文座標表示
  const pagesRef = useRef(null)
  const currentPageRef = useRef(currentPage)
  currentPageRef.current = currentPage
  const [currentScrollTop, setCurrentScrollTop] = useState(0);
  const [lastScrollTop, setLastScrollTop] = useState(0);

  // 不切頁：圖片與文字一起等比縮小，整題塞進一個畫面。
  // 切頁：文字維持字級不縮，圖片上限為頁高 IMAGE_MAX_RATIO，放不下的行依序排到下一頁。
  const isPaged = !!shouldSplited && splitType !== SplitType.NO_SPLIT;

  // 畫記依「題 × 頁」各自保存：groupKey 由 tabIndex 決定（ExtendedContentEvent 的
  // getAnnotationGroupKey），內層再用 currentSplitedPageIndex 區分頁。不切頁時頁碼恆為 0。
  const saveAnnotation = useCallback((tab, page) => {
    EventBus.emit({
      eventBusType: EventBusType.ExtendedContent,
      event: CanvasEvent.SplitedPageSaveCanvasEvent,
      payload: { currentSplitedPageIndex: page, tabIndex: tab },
    });
  }, []);

  const onTabClickHandler = useCallback((index) => {
    if (index === tabIndex) return;
    // 先保存離開前這一題、這一頁的畫記，再切換
    saveAnnotation(tabIndex, currentPage);
    setState({ tabIndex: index });
    // 頁碼必須和 tabIndex 在同一批更新裡歸零。若慢一個 render，會多出「新題 × 舊頁碼」的
    // 中間狀態，下方畫記 effect 會把它誤判成同一題內翻頁而把畫記存錯頁（同 NBK-1700）
    extendedContentDispatch({
      type: types.SET_EXTENDED_CONTENT_CURRENT_PAGE,
      payload: { currentPage: 0 },
    });
  }, [tabIndex, currentPage, setState, saveAnnotation, extendedContentDispatch]);

  // 切題／翻頁後載入該題該頁自己的畫記（延遲一個 tick，確保 save 已寫入）。
  // 首次 mount 時 ref 等於當前值，不觸發載入，以保留互動物件既有的畫記。
  // 關閉小視窗時 onCloseHandler 會把 currentPage 歸 0，所以 mount 時頁碼不會殘留。
  const prevTabRef = useRef(tabIndex);
  const prevPageRef = useRef(currentPage);
  useEffect(() => {
    const tabChanged = prevTabRef.current !== tabIndex;
    const pageChanged = prevPageRef.current !== currentPage;
    if (!tabChanged && !pageChanged) return;

    // 切題的 save 已在 onTabClickHandler 做完，這裡只補同一題內翻頁的 save
    if (!tabChanged) saveAnnotation(tabIndex, prevPageRef.current);

    const timer = setTimeout(() => {
      // ref 代表「畫布上目前是哪一題哪一頁的畫記」，只在 import 真的執行時才更新；
      // 被 cleanup 取消的中間 render 不能推進它（同 NBK-1700）
      prevTabRef.current = tabIndex;
      prevPageRef.current = currentPage;
      EventBus.emit({
        eventBusType: EventBusType.ExtendedContent,
        event: CanvasEvent.SplitedPageImportJSONEvent,
        payload: { currentSplitedPageIndex: currentPage, tabIndex },
      });
    }, 0);
    return () => clearTimeout(timer);
  }, [tabIndex, currentPage, saveAnnotation]);

  useEffect(() => {
    // 小視窗內容上下頁分段（不切頁模式：捲動 .topic）。
    // 切頁模式由 ExtendedContentScrollPositionButton 直接改 currentPage，這裡不捲動
    if (isPaged) return;
    switch (scrollPosition) {
      case ScrollPositionType.Next: {
        setLastScrollTop(topicRef.current.scrollTop);
        topicRef.current.scrollTop += topicRef.current.offsetHeight*ScrollLength;
        setCurrentScrollTop(topicRef.current.scrollTop);

        break;
      }
      case ScrollPositionType.Prev: {
        setLastScrollTop(topicRef.current.scrollTop);
        topicRef.current.scrollTop -= topicRef.current.offsetHeight*ScrollLength;
        setCurrentScrollTop(topicRef.current.scrollTop);
        break;
      }
      default: {
        scrollPositionButtonBehavior({currentScroll: currentScrollTop, lastScroll: lastScrollTop, coordinate: ScrollCoordinate.Y})
        break;
      }
    }
  }, [scrollPosition]);

  // 還原縮放（量測必須在未縮放的版面上進行，否則高度會失真）
  const resetPanelScale = useCallback((panel) => {
    if (!panel) return;
    panel.style.transform = '';
    panel.style.transformOrigin = '';
    panel.style.width = '';
    panel.style.height = '';
    // 每次重新適配都從乾淨狀態開始量測，保底樣式不可沿用上一輪的結果
    panel.classList.remove(styles.contentScroll);
  }, []);

  // 內容超出小視窗時整頁等比縮小。
  // 用 transform: scale() 而非調整 font-size：font-size 只縮得動「繼承容器字級」的文字，
  // 標題圖片（.title img）與 dangerouslySetInnerHTML 注入的絕對單位字級都不會跟著縮，
  // scale 則會等比縮放整個子樹，圖片和文字一起變小。作法沿用 VocabularyExample（NBK-1439）。
  const fitPanelToContainer = useCallback((panel) => {
    if (!panel) return;

    // 套用比例：同時把 width / height 放大為 1/s，讓內容在放大的版面中重排後再整體縮回，
    // 縮放後剛好填滿外框，右側與下方不會留空隙。
    const applyScale = (s) => {
      if (s >= 1) {
        resetPanelScale(panel);
      } else {
        const pct = (100 / s) + '%';
        panel.style.transformOrigin = 'top left';
        panel.style.width = pct;
        panel.style.height = pct;
        panel.style.transform = 'scale(' + s + ')';
      }
      void panel.offsetHeight;
    };

    const fits = () => panel.scrollHeight <= panel.clientHeight + 0.5;

    // 先還原再量測，避免沿用上一次的縮放結果
    applyScale(1);
    if (panel.clientHeight <= 0 || fits()) return;

    // 二分搜尋「最大且仍塞得下」的比例：s 越小版面越大越容易容納
    const MIN_SCALE = 0.3;
    let lo = MIN_SCALE;
    let hi = 1;
    let best = null;

    for (let i = 0; i < 12; i++) {
      const mid = (lo + hi) / 2;
      applyScale(mid);
      if (fits()) {
        best = mid;
        lo = mid;
      } else {
        hi = mid;
      }
    }

    if (best !== null) {
      applyScale(best);
    } else {
      // 連 MIN_SCALE 都塞不下：維持 panel 原尺寸，改讓文字區自己捲動。
      // 不再用 height: auto —— 那會切斷 flex 高度鏈，.title 的 min-height 與
      // img 的 max-height 百分比一起失效，圖片重新撐爆版面，而且 .topic 會
      // 長出卷軸、往下拉是一段空白（NBK-1702 的第三個症狀）。
      resetPanelScale(panel);
      panel.classList.add(styles.contentScroll);
    }
  }, [resetPanelScale]);

  // ===== 切頁模式 =====

  // 回到「第一頁、未位移、未裁切」的版面，量測前與離開切頁模式時使用
  const resetPageView = useCallback(() => {
    if (titleRef.current) titleRef.current.style.display = '';
    if (innerRef.current) {
      innerRef.current.style.transform = '';
      innerRef.current.style.clipPath = '';
    }
  }, []);

  // 套用某一頁：第一頁顯示圖片，第二頁起隱藏；內文整體上移到該頁起點，
  // 再用 clip-path 只露出 [start, end]，下一頁的第一行不會露出半截。
  // 斷點都落在兩行交界的中點（paging.js），所以不需要再額外留邊。
  // 不拆 DOM，所以音檔按鈕、答案 toggle、dangerouslySetInnerHTML 的內容都不受影響。
  const applyPage = useCallback((page) => {
    const pages = pagesRef.current;
    const title = titleRef.current;
    const inner = innerRef.current;
    if (!pages || !title || !inner) return;

    const index = Math.max(0, Math.min(page, pages.length - 1));
    const { start, end } = pages[index];
    title.style.display = index === 0 ? '' : 'none';
    inner.style.transform = start ? `translateY(${-start}px)` : '';
    inner.style.clipPath = `inset(${start}px 0 ${Math.max(0, inner.offsetHeight - end)}px 0)`;
  }, []);

  // 量測並計算各頁範圍。文字字級不動，只限制圖片高度。
  const paginate = useCallback(() => {
    const panel = panelRef.current;
    const title = titleRef.current;
    const viewport = contentRef.current;
    const inner = innerRef.current;
    if (!panel || !title || !viewport || !inner) return null;

    resetPageView();
    const panelHeight = panel.clientHeight;
    if (panelHeight <= 0) return null;

    // 圖片上限直接寫成 px：CSS 的 max-height 百分比要父層有確定高度才解析得出來，
    // 而 .title 在切頁模式下高度由圖片本身決定（auto），寫百分比會失效
    const img = title.querySelector('img');
    if (img) img.style.maxHeight = Math.floor(panelHeight * IMAGE_MAX_RATIO) + 'px';

    // 第一頁文字可用高度（扣掉圖片），與第二頁起的整頁高度（隱藏圖片）
    const firstCapacity = viewport.clientHeight;
    title.style.display = 'none';
    const restCapacity = viewport.clientHeight;
    title.style.display = '';

    // 行框座標換回內文的本地座標：小視窗本身可能被 transform: scale()（popWindowScale、
    // 四格放大），getBoundingClientRect 拿到的是縮放後的視覺座標
    const innerRect = inner.getBoundingClientRect();
    const scale = inner.offsetHeight > 0 ? innerRect.height / inner.offsetHeight : 1;
    const rects = [];
    const pushRect = (r) => {
      if (r.height <= 0) return;
      rects.push({ top: (r.top - innerRect.top) / scale, bottom: (r.bottom - innerRect.top) / scale });
    };

    // 文字：Range.getClientRects 對每一行各回傳一個框，長段落也能在行與行之間斷開
    const walker = document.createTreeWalker(inner, NodeFilter.SHOW_TEXT);
    const range = document.createRange();
    let node = walker.nextNode();
    while (node) {
      if (node.textContent.trim()) {
        range.selectNodeContents(node);
        Array.from(range.getClientRects()).forEach(pushRect);
      }
      node = walker.nextNode();
    }
    // 內文裡的圖片、公式等非文字元素也要佔行
    inner.querySelectorAll('img, svg, video, canvas, iframe, math').forEach((el) => pushRect(el.getBoundingClientRect()));

    return splitIntoPages(mergeLineBands(rects), firstCapacity, restCapacity);
  }, [resetPageView]);

  // 切題／字級／容器尺寸變化、圖片或字型載入時重新分頁
  useLayoutEffect(() => {
    const panel = panelRef.current;
    if (!isPaged || !panel) return;

    const run = () => {
      const pages = paginate();
      if (!pages) return;
      pagesRef.current = pages;
      // 頁數變了 reducer 會一併把 currentPage 歸零（extendedContentReducer）
      extendedContentDispatch({
        type: types.SET_EXTENDED_CONTENT_TOTAL_PAGES,
        payload: { totalPages: pages.length },
      });
      applyPage(currentPageRef.current);
    };

    // 第一次同步執行（layout effect，繪製前），切題時不會先閃出未裁切的全文
    run();
    let rafId = null;
    const schedule = () => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(run);
    };

    // 與縮放模式相同的觸發點；套用分頁只改 .title 的 display 與內文的 transform/clip-path，
    // 不會改變 .topic 的尺寸，觀察它不會形成迴圈
    const wrapper = topicRef.current;
    let ro = null;
    if (wrapper && typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(schedule);
      ro.observe(wrapper);
    }
    const pendingImages = Array.from(panel.querySelectorAll('img')).filter((img) => !img.complete);
    pendingImages.forEach((img) => {
      img.addEventListener('load', schedule);
      img.addEventListener('error', schedule);
    });
    let cancelled = false;
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => { if (!cancelled) schedule(); });
    }

    return () => {
      cancelled = true;
      cancelAnimationFrame(rafId);
      if (ro) ro.disconnect();
      pendingImages.forEach((img) => {
        img.removeEventListener('load', schedule);
        img.removeEventListener('error', schedule);
      });
    };
  }, [isPaged, tabIndex, fontSize, paginate, applyPage, extendedContentDispatch]);

  // 翻頁：只套用已算好的範圍，不重新量測
  useLayoutEffect(() => {
    if (isPaged) applyPage(currentPage);
  }, [isPaged, currentPage, applyPage]);

  // ===== 不切頁模式 =====

  // 內容／字級／容器尺寸變化時重新適配：圖片與文字一起等比縮小，整題塞進一個畫面。
  // 原本這裡對不切頁直接 return，但 .panel 的 height:100% + overflow:hidden（NBK-1657 加入）
  // 不分模式都生效，不切頁時超出的文字因此被截掉，既不縮放也捲不到。
  // isPaged 在 deps 裡：分頁設定可能在 mount 後才寫入 store，寫入時要切換模式。
  useEffect(() => {
    const panel = panelRef.current;
    if (!panel) return;

    if (isPaged) {
      // 切頁模式不縮放，清掉可能殘留的縮放樣式
      resetPanelScale(panel);
      return;
    }
    // 清掉切頁模式可能殘留的位移、裁切與圖片上限
    resetPageView();
    const pagedImg = titleRef.current && titleRef.current.querySelector('img');
    if (pagedImg) pagedImg.style.maxHeight = '';

    const run = () => fitPanelToContainer(panel);
    let rafId = requestAnimationFrame(run);
    const schedule = () => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(run);
    };

    // 只觀察外層 .topic：panel 為 height:100%，縮放不會改變外框尺寸，
    // 避免「改 panel 尺寸 → 觸發 observer → 再改 panel」的無限迴圈。
    const wrapper = topicRef.current;
    let ro = null;
    if (wrapper && typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(schedule);
      ro.observe(wrapper);
    }

    // 圖片載入完成前高度趨近 0，首次量測會誤判「塞得下」而不縮放；
    // 而 observer 只看 .topic，圖片在 panel 內載入並不會改變它的尺寸，
    // 因此不會有任何後續觸發。這裡逐一補上 load/error 當觸發點。
    // （不改回觀察子元素：套用縮放時會改 panel 的 width/height，
    //   子元素跟著重排，觀察子元素會形成無限迴圈。）
    const pendingImages = Array.from(panel.querySelectorAll('img')).filter((img) => !img.complete);
    pendingImages.forEach((img) => {
      img.addEventListener('load', schedule);
      img.addEventListener('error', schedule);
    });

    // 網頁字型載入完成會改變文字高度，同樣需要重算
    let cancelled = false;
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => { if (!cancelled) schedule(); });
    }

    return () => {
      cancelled = true;
      cancelAnimationFrame(rafId);
      if (ro) ro.disconnect();
      pendingImages.forEach((img) => {
        img.removeEventListener('load', schedule);
        img.removeEventListener('error', schedule);
      });
    };
  }, [tabIndex, fontSize, isPaged, fitPanelToContainer, resetPanelScale, resetPageView]);

  const targetScrollHeight = topicRef.current? topicRef.current.scrollHeight : 0

  useEffect(()=> {
      // 切頁模式的按鈕狀態由 ExtendedContentScrollPositionButton 依 currentPage／totalPages 計算
      // （聽力稿 selfManagedPagination: false）。這裡是捲動式的按鈕狀態，切頁時 .topic 不捲動，
      // 若照跑會一直送出 None，把依頁數算好的狀態蓋掉，上下頁按鈕就按不了
      if (isPaged) return;
      const updateScrollButtonState = () => {
        const offsetHeight = topicRef.current.offsetHeight
        const scrollHeight = topicRef.current.scrollHeight
        const scrollTop = topicRef.current.scrollTop
        const headCond = scrollTop === 0
        const bottomCond = (scrollHeight - offsetHeight - scrollTop) < 1
        if (headCond) {
          ScrollButtonStateBehavior(ScrollPositionButtonStateType.Head)
        }else if(bottomCond) {
          ScrollButtonStateBehavior(ScrollPositionButtonStateType.Bottom)
        } else {
          ScrollButtonStateBehavior(ScrollPositionButtonStateType.Middle)
        }
      }
      if (topicRef && topicRef.current) {
        if(topicRef.current.scrollHeight === topicRef.current.offsetHeight) {
          ScrollButtonStateBehavior(ScrollPositionButtonStateType.None)
        } else {
          ScrollButtonStateBehavior(ScrollPositionButtonStateType.Head)
        }
        topicRef.current.scrollTop = 0
        topicRef.current.addEventListener("scroll", updateScrollButtonState, false)
      }
      const topic = topicRef.current
      return () => {
        if (topic) topic.removeEventListener('scroll', updateScrollButtonState, false)
      }
  },[targetScrollHeight,fontSize,tabIndex,isPaged])

  return (
    <div className={classnames(styles.hearingDraft, styles[fontSize])}>
      <div className={styles.main}>
        <AudioControlsButton src={ExtendedResourceRepository.getDataSrc({
          bookId,
          pathName: contents[tabIndex].contentSound
        })} />
        <div className={styles.topic} ref={topicRef}>
          {/* panel：不切頁時是被 scale 的對象；切頁時是一頁的外框。.topic 維持原尺寸 */}
          <div className={classnames(styles.panel, { [styles.paged]: isPaged })} ref={panelRef}>
            <div className={styles.title} ref={titleRef}>
              {/* key 綁 tabIndex 強制重建節點：沿用同一個 <img> 只換 src 時，
                  瀏覽器會保留舊圖的佈局尺寸直到新圖解碼完成，下方 effect 的
                  第一次量測就會拿到上一題的圖高而誤判，切頁後圖文才會跑掉 */}
              <img
                key={tabIndex}
                src={ExtendedResourceRepository.getDataSrc({ bookId, pathName: contents[tabIndex].topic })}
                alt=""
              />
            </div>
            <div className={styles.content} ref={contentRef}>
              {/* 切頁模式的位移與裁切對象；.content 本身是一頁的可視範圍 */}
              <div className={styles.contentInner} ref={innerRef}>
              {
                contents[tabIndex].content.map((list, index) => {
                  return (
                    <div
                      className={styles.list}
                      // 不可用內容字串當 key：同一題常有重複的行（空行、相同的角色標籤），
                      // key 重複時 React 在切題 diff 會留下殘留節點，行數每切一次就變多。
                      // 內容是靜態清單、順序不變，用位置即可；前綴 tabIndex 讓切題時整批重建，
                      // 與上方 img 的 key 一致
                      key={`${tabIndex}-${index}`}
                      dangerouslySetInnerHTML={{ __html: list }}
                    />
                  )
                })
              }
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* 阻止冒泡：ExtendedContentContainer 的 clearCanvas 會用 class 名稱（tab/list/voca）
          判斷要不要清空畫記，tab 按鈕與其間隙都會命中，會把畫記整張清掉 */}
      <div className={classnames(styles.tab)} onClick={(e) => e.stopPropagation()}>
        {
          contents.map((item, index) => <div className={classnames(styles.list, {
            [styles.active]: tabIndex === index
          })} key={index} onClick={() => onTabClickHandler(index)}>{index + 1}</div>)
        }
      </div>
    </div>
  );
};

export default HearingDraft;
